// src/stores/useImageryStackStore.js
//
// Imagery stack + global time cursor + catalog. The store owns user intent;
// the resolver turns intent into render specs; the reconcile step writes
// those specs into useLayerRegistryStore so the existing map pipeline picks
// them up without modification.
//
// State:
//   catalog        Series[] from /imagery/footprints (loaded on init)
//   stack          ImageryStackEntry[] — user's active imagery layers
//   cursor         { global_date, snap_policy, stale_threshold_months }
//   loading        bool — true while catalog is being fetched
//   error          string|null — last fetch error
//
// Every mutation that affects what should render calls reconcile(), which
// runs the resolver and syncs useLayerRegistryStore. State is intentionally
// flat; we don't try to be clever about partial reconciliation. The resolver
// is cheap and the stack is small (tens of entries max).

import { create } from "zustand";
import { nanoid } from "nanoid";

import { resolveImageryStack, makeSeriesKey, acquisitionOnOrBefore, resolveReferenceLane, compareReferenceLanes } from "../layers/imagery/resolveImageryStack";
import { groupFootprintsIntoSeries, groupReferenceSeries } from "../layers/imagery/groupFootprintsIntoSeries";
import { useLayerRegistryStore } from "./useLayerRegistryStore";
import { useAuthStore } from "./useAuthStore";

// ─────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────

const DEFAULT_CURSOR = {
    global_date: currentMonthKey(),       // "YYYY_MM" of right now
    snap_policy: "on_or_before",
    stale_threshold_months: 6,
};

function currentMonthKey() {
    const d = new Date();
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${y}_${m}`;
}

// Per-layer raster display adjustments — analyst visual aid, mirrors `opacity`.
// Identity defaults are no-ops (the raster renders untouched). Each field maps
// 1:1 to a MapLibre raster paint prop; ranges match those props exactly so the
// store is the single clamp authority (the resolver and sync hook trust it).
const IDENTITY_ADJUST = Object.freeze({
    brightnessMin: 0,   // raster-brightness-min   0..1    (lifts shadows)
    contrast: 0,        // raster-contrast        -1..1
    saturation: 0,      // raster-saturation      -1..1
    hueRotate: 0,       // raster-hue-rotate       0..359  (degrees)
});

function makeIdentityAdjust() {
    return { ...IDENTITY_ADJUST };
}

function clampRange(v, lo, hi) {
    const n = Number(v);
    if (!Number.isFinite(n)) return lo;
    return Math.max(lo, Math.min(hi, n));
}

// Normalize an adjust object: fill missing fields from identity, clamp each to
// its prop's range. Tolerant of partial/legacy entries (entry.adjust === undefined).
function clampAdjust(a) {
    const src = a || IDENTITY_ADJUST;
    return {
        brightnessMin: clampRange(src.brightnessMin ?? 0, 0, 1),
        contrast: clampRange(src.contrast ?? 0, -1, 1),
        saturation: clampRange(src.saturation ?? 0, -1, 1),
        hueRotate: clampRange(src.hueRotate ?? 0, 0, 359),
    };
}

// ─────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────

export const useImageryStackStore = create((set, get) => ({
    // series: the 10 cm lane, what every catalog consumer reads. reference:
    // Sentinel-2 composites (product_class 'reference'), kept apart (B4).
    catalog: { series: [], reference: [], aoi_counties: null },
    stack: [],
    cursor: DEFAULT_CURSOR,
    loading: false,
    error: null,

    // NEW: tracks which dataset_keys we currently hold sessions for.
    // Updated by _reconcile. Useful for debugging and for the lifecycle diff.
    _activeDatasetKeys: new Set(),

    compareMode: {
        enabled: false,
        // The two layer_ids on each side, set by enterCompareForDates.
        left_layer_id: null,
        right_layer_id: null,
        // The subset of those this mode CREATED, so exit removes what it added
        // and leaves entries the user already had. See enterCompareForDates.
        created_layer_ids: [],
        // Slider position 0..1, where 0.5 = centered.
        // Persisting this means the slider stays where the user put it
        // across compare-on/off cycles.
        slider_position: 0.5,
    },


    // ─── Catalog ───────────────────────────────────

    /**
     * Fetch /imagery/footprints, group into Series[], set catalog, reconcile.
     * Accepts an optional fetcher for testability.
     */
    loadCatalog: async (options = {}) => {

        const fetcher = typeof options === "function"
            ? options
            : () => defaultFootprintsFetcher(options);

        const gen = (get()._loadGen ?? 0) + 1;
        set({ loading: true, error: null, _loadGen: gen });

        try {
            const fc = await fetcher();

            if (get()._loadGen !== gen) {
                return;
            }

            const series = groupFootprintsIntoSeries(fc);
            const reference = groupReferenceSeries(fc);
            // FC-level aoi_counties (county geoms for thumbnail rendering)
            // is shipped by /imagery/footprints when operating_aoi is set.
            // Null preserves the "no AOI context" signal that drives the
            // coverage line's hide/show logic in SeriesCoverageLine.
            const aoi_counties = Array.isArray(fc?.aoi_counties)
                ? fc.aoi_counties
                : null;
            set({ catalog: { series, reference, aoi_counties }, loading: false });
            get()._reconcile();
        } catch (e) {
            console.log("[imagery] loadCatalog ERROR", e);
            if (get()._loadGen !== gen) return;
            set({ loading: false, error: String(e?.message || e) });
        }

        // After setting catalog: align global cursor to catalog max if no cursor
        // has been set or the current cursor is outside the new range.
        const range = get().getCatalogTimeRange();
        const currentCursor = get().cursor.global_date;

        if (range.max && (!currentCursor || currentCursor > range.max)) {
            set(state => ({ cursor: { ...state.cursor, global_date: range.max } }));
            get()._reconcile();
        }
    },

    setCatalog: (catalog) => {
        set({ catalog });
        get()._reconcile();
    },

    // ─── Stack mutations ───────────────────────────

    /**
     * Add a layer to the stack.
     *
     * If the same (series_key, resolved acquisition) is already in the stack,
     * this is a no-op (returns the existing layer_id). To force a second copy,
     * call with { force: true } or with a different pinned_date.
     *
     * @param {string} series_key
     * @param {{
     *   time_mode?: 'global'|'pinned',
     *   pinned_date?: string|null,
     *   opacity?: number,
     *   visible?: boolean,
     *   force?: boolean,
     * }} opts
     * @returns {string|null} layer_id, or null if series not in catalog
     */
    addLayer: (series_key, opts = {}) => {
        const { catalog, stack, cursor } = get();
        const series = catalog.series.find((s) => s.series_key === series_key);
        if (!series) return null;

        const time_mode = opts.time_mode ?? "global";
        const pinned_date = time_mode === "pinned" ? (opts.pinned_date ?? null) : null;

        // Dedupe: same series + same effective acquisition date = no-op.
        if (!opts.force) {
            const effectiveDate = effectiveAcquisitionDate(series, time_mode, pinned_date, cursor);
            const existing = stack.find((e) => {
                if (e.series_key !== series_key) return false;
                if (e.time_mode !== time_mode) return false;          // ← ADD
                if (time_mode === "pinned" && e.pinned_date !== pinned_date) return false;   // ← ADD
                const eDate = effectiveAcquisitionDate(series, e.time_mode, e.pinned_date, cursor);
                return eDate === effectiveDate;
            });
            if (existing) return existing.layer_id;
        }

        const layer_id = nanoid(10);
        const newEntry = {
            layer_id,
            series_key,
            time_mode,
            pinned_date,
            last_pinned_date: pinned_date,
            opacity: opts.opacity ?? 1,
            visible: opts.visible ?? true,
            // Per-layer raster adjustments. Defaults to identity (no-op). Lives on
            // the stack entry alongside opacity so it survives re-adds (AOI change,
            // token refresh, paneFilter) via the same resolve→register→sync path.
            adjust: opts.adjust ? clampAdjust(opts.adjust) : makeIdentityAdjust(),
            z_index: stack.length,         // append at top of stack
        };

        set({ stack: [...stack, newEntry] });
        get()._reconcile();
        return layer_id;
    },

    removeLayer: (layer_id) => {
        const { stack } = get();
        const next = stack
            .filter((e) => e.layer_id !== layer_id)
            .map((e, i) => ({ ...e, z_index: i }));
        set({ stack: next });

        // Auto-exit compare mode if we drop below 2 imagery layers OR if
        // either of the two compared layers was removed.
        const cm = get().compareMode;
        if (cm.enabled) {
            const stillCompareable =
                next.length >= 2 &&
                next.some(e => e.layer_id === cm.left_layer_id) &&
                next.some(e => e.layer_id === cm.right_layer_id);
            if (!stillCompareable) {
                set(state => ({
                    compareMode: {
                        ...state.compareMode,
                        enabled: false,
                        left_layer_id: null,
                        right_layer_id: null,
                        // Ownership is dropped with the mode. The layer that
                        // triggered this auto-exit is already being removed,
                        // and its partner belongs to whoever else referenced
                        // it — exitCompareMode must not go looking for either.
                        created_layer_ids: [],
                    },
                }));
            }
        }


        get()._reconcile();
    },

    setLayerOpacity: (layer_id, opacity) => {
        const clamped = Math.max(0, Math.min(1, opacity));
        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id ? { ...e, opacity: clamped } : e
            ),
        }));
        get()._reconcile();
    },

    // Shallow-merge `patch` into the entry's adjust, clamping each field to its
    // prop range. Mirrors setLayerOpacity: mutate the stack entry, then reconcile
    // so the new adjust flows resolve→register→sync onto the live raster layer.
    setLayerAdjust: (layer_id, patch) => {
        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id
                    ? { ...e, adjust: clampAdjust({ ...IDENTITY_ADJUST, ...e.adjust, ...patch }) }
                    : e
            ),
        }));
        get()._reconcile();
    },

    // Return all four fields to identity (raster renders untouched).
    resetLayerAdjust: (layer_id) => {
        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id ? { ...e, adjust: makeIdentityAdjust() } : e
            ),
        }));
        get()._reconcile();
    },

    //
    // Enter compare mode using the top two imagery layers in stack order.
    //  No-op if fewer than 2 layers exist.
    //
    /**
     * COMPARE IS ENTERED FROM THE TIME STRIP, BY DATE.
     *
     * It used to take "the top two entries in the stack", which meant the only
     * way to reach it was to switch on two imagery layers by hand in the legacy
     * IMAGERY panel. That coupling is what kept the panel alive: compare needed
     * the user to assemble its inputs before it would turn on.
     *
     * A comparison is between two DATES, and the strip already knows which
     * dates exist and which of them cover the view. So it names the pair and
     * this builds the two pinned entries to render them, rather than asking the
     * user to construct the entries and then inferring their intent from
     * z-order.
     *
     * ENTRIES CREATED HERE ARE OWNED HERE. exitCompareMode removes exactly the
     * ones it added and leaves anything the user had already switched on. A
     * comparison is a mode, not a stack edit, and leaving two pinned layers
     * behind on exit would be a state the user never asked for and has no
     * obvious way to undo.
     *
     * Left is the OLDER date. A swipe reads left-to-right as time, and having
     * that be arbitrary would make "which side is which" a thing to remember.
     */
    enterCompareForDates: (series_key, dateA, dateB) => {
        if (!series_key || !dateA || !dateB || dateA === dateB) return false;
        const series = get().catalog.series.find((s) => s.series_key === series_key);
        if (!series) return false;

        const [older, newer] = dateA < dateB ? [dateA, dateB] : [dateB, dateA];

        const before = new Set(get().stack.map((e) => e.layer_id));
        const leftId = get().addLayer(series_key, { time_mode: "pinned", pinned_date: older });
        const rightId = get().addLayer(series_key, { time_mode: "pinned", pinned_date: newer });
        if (!leftId || !rightId || leftId === rightId) return false;

        // Only the ids that did not exist a moment ago. addLayer dedupes, so a
        // date the user already had switched on returns THEIR entry and must
        // not be torn down when compare exits.
        const created = [leftId, rightId].filter((id) => !before.has(id));

        set((state) => ({
            compareMode: {
                ...state.compareMode,
                enabled: true,
                left_layer_id: leftId,
                right_layer_id: rightId,
                created_layer_ids: created,
            },
        }));
        // The reference lane follows the mode: one per pane from here on.
        get()._reconcile();
        return true;
    },

    exitCompareMode: () => {
        const created = get().compareMode.created_layer_ids ?? [];
        set((state) => ({
            compareMode: {
                ...state.compareMode,
                enabled: false,
                left_layer_id: null,
                right_layer_id: null,
                created_layer_ids: [],
            },
        }));
        // After the mode is off, so the panes stop filtering before their
        // layers disappear. removeLayer reconciles.
        for (const id of created) get().removeLayer(id);
        // And once more for the reference lane, back to the global cursor:
        // with no created entries, nothing above has reconciled.
        get()._reconcile();
    },

    setCompareSliderPosition: (position) => {
        const clamped = Math.max(0, Math.min(1, position));
        set((state) => ({
            compareMode: { ...state.compareMode, slider_position: clamped },
        }));
    },

    setLayerVisible: (layer_id, visible) => {
        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id ? { ...e, visible } : e
            ),
        }));
        get()._reconcile();
    },

    pinLayer: (layer_id, pinned_date) => {
        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id
                    ? { ...e, time_mode: "pinned", pinned_date }
                    : e
            ),
        }));
        get()._reconcile();
    },

    unpinLayer: (layer_id) => {
        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id
                    ? { ...e, time_mode: "global", pinned_date: null }
                    : e
            ),
        }));
        get()._reconcile();
    },

    /**
     * Reorder the stack by moving the entry with id `activeId` to the position
     * of the entry with id `overId`. z_index is re-numbered 0..n-1 afterward.
     */
    reorderStack: (activeId, overId) => {
        const { stack } = get();
        const from = stack.findIndex((e) => e.layer_id === activeId);
        const to = stack.findIndex((e) => e.layer_id === overId);
        if (from < 0 || to < 0 || from === to) return;

        const next = [...stack];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        const renumbered = next.map((e, i) => ({ ...e, z_index: i }));

        set({ stack: renumbered });
        get()._reconcile();
    },

    // ─── Cursor ────────────────────────────────────

    setGlobalCursor: (global_date) => {
        set((state) => ({ cursor: { ...state.cursor, global_date } }));
        get()._reconcile();
    },

    setStaleThreshold: (stale_threshold_months) => {
        set((state) => ({ cursor: { ...state.cursor, stale_threshold_months } }));
        get()._reconcile();
    },

    // ─── Selectors ─────────────────────────────────

    /**
     * Run the resolver against current state. Pure, no side effects.
     * Useful for components that need the specs directly (e.g. the swipe
     * compare UI that pulls two specs out of the stack).
     * @returns {import('../layers/imagery/types').RenderSpec[]}
     */
    getRenderSpecs: () => {
        const { stack, cursor, catalog } = get();
        return resolveImageryStack({ stack, cursor, catalog });
    },

    /**
     * The reference lane's specs (B4, D5): Sentinel-2 under the 10 cm lane,
     * following the global cursor. See resolveReferenceLane.
     */
    getReferenceSpecs: () => {
        const { cursor, catalog } = get();
        // In compare, a lane per pane on that pane's pinned date, in place of
        // the global one (B4); each pane draws only its own (paneImageryIds).
        return compareReferenceLanes(get()) ?? resolveReferenceLane({ cursor, catalog });
    },

    /**
     * Returns the available time range across the catalog:
     *   { min, max, dates }
     * where dates is the sorted unique list of acquisition_dates across all
     * series. Useful for sizing/labeling time controls.
     *
     * Returns { min: null, max: null, dates: [] } when the catalog is empty.
     */
    getCatalogTimeRange: () => {
        const { catalog } = get();
        const set = new Set();
        for (const series of catalog.series ?? []) {
            for (const acq of series.acquisitions ?? []) {
                if (acq.acquisition_date) set.add(acq.acquisition_date);
            }
        }
        const dates = [...set].sort();
        return {
            min: dates[0] ?? null,
            max: dates[dates.length - 1] ?? null,
            dates,
        };
    },

    /* For each (series_key, effective_date) currently in the stack, returns
    * what's driving it. Used by the imagery catalog to show toggle state.
    *
    * Returns a Map keyed by `${series_key}::${effective_date}` → {
    *   layer_id: string,    // the stack entry's layer_id (for removal lookup)
    *   driver: 'pin' | 'follow',  // pin wins ties
    * }
    *
    * Pin-driven entries take precedence: if both a pin and a follower land
    * on the same date, the toggle shows as pin-driven. This is intentional
    * — pin is the more permanent, intentional mode.
    */
    getCatalogRenderMap: () => {
        const { stack, cursor, catalog } = get();
        const result = new Map();

        for (const entry of stack) {
            const series = catalog.series.find((s) => s.series_key === entry.series_key);
            if (!series) continue;

            const effectiveDate = effectiveAcquisitionDate(
                series,
                entry.time_mode,
                entry.pinned_date,
                cursor
            );
            if (!effectiveDate) continue;

            const key = `${entry.series_key}::${effectiveDate}`;
            const driver = entry.time_mode === "pinned" ? "pin" : "follow";

            // Pin wins ties: only overwrite if the existing entry isn't a pin.
            const existing = result.get(key);
            if (!existing || (existing.driver === "follow" && driver === "pin")) {
                result.set(key, { layer_id: entry.layer_id, driver });
            }
        }

        return result;
    },

    /*** Switch a layer between "pinned" and "follow_cursor" modes.
    *
    * Switching to "pinned":
    *   The layer's pinned_date is set to whatever date it currently resolves to.
    *   This means a follower-mode layer at cursor=2025_09 becomes pinned to
    *   "2025_09" — visually identical, but no longer responds to cursor changes.
    *
     * Switching to "follow_cursor":
     *   pinned_date is cleared. The layer's effective date now tracks
     *   cursor.global_date.
    *
    * No-op if the layer is already in the requested mode or if the layer
    * can't be found in the stack.
    */
    setLayerTimeMode: (layer_id, mode) => {
        if (mode !== "pinned" && mode !== "follow_cursor") return;

        const { stack, cursor, catalog } = get();
        const entry = stack.find((e) => e.layer_id === layer_id);
        if (!entry) return;
        if (entry.time_mode === mode) return;

        let next_pinned_date = entry.pinned_date;
        let next_last_pinned_date = entry.last_pinned_date;

        if (mode === "pinned") {
            // Restore last_pinned_date if present; otherwise use current effective date.
            if (entry.last_pinned_date) {
                next_pinned_date = entry.last_pinned_date;
            } else {
                const series = catalog.series.find((s) => s.series_key === entry.series_key);
                if (!series) return;
                next_pinned_date = effectiveAcquisitionDate(
                    series,
                    entry.time_mode,
                    entry.pinned_date,
                    cursor
                );
                if (!next_pinned_date) return;
            }
        } else {
            // Switching to follow_cursor — save current pinned_date for later restoration.
            next_last_pinned_date = entry.pinned_date;
            next_pinned_date = null;
        }

        set((state) => ({
            stack: state.stack.map((e) =>
                e.layer_id === layer_id
                    ? { ...e, time_mode: mode, pinned_date: next_pinned_date, last_pinned_date: next_last_pinned_date }
                    : e
            ),
        }));
        get()._reconcile();
    },


    // ─── Reconcile (internal) ──────────────────────

    /**
     * Run the resolver and sync useLayerRegistryStore. Called automatically
     * after every state mutation that affects what should render.
     *
     * Reconciliation strategy:
     *   - Build the desired registry entry for each RenderSpec.
     *   - Use registerLayers({ replaceScope: 'imagery' }) to atomically swap
     *     all imagery-scoped entries. This means stale imagery layers from a
     *     previous reconcile are removed in the same set() call as new ones
     *     are added — no visible flicker, no orphaned entries.
     */
    _reconcile: () => {
        const specs = get().getRenderSpecs();
        const { catalog } = get();
        // The reference lane rides in the same "imagery" scope, so one
        // replaceScope call mounts and retires both. group "reference" puts it
        // under the footprint wash and the 10 cm lane (D5's draw order), and
        // lane "reference" keeps it out of the layer panel and layer sets.
        const referenceLayers = buildRegistryLayersFromSpecs(
            get().getReferenceSpecs(),
            { series: catalog.reference ?? [] }
        ).map((l) => ({ ...l, group: "reference", lane: "reference" }));
        const registryLayers = [
            ...referenceLayers,
            ...buildRegistryLayersFromSpecs(specs, catalog),
        ];
        useLayerRegistryStore.getState().registerLayers(registryLayers, {
            replaceScope: "imagery",
        });
    },
}));

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function getOptionalSessionManager() {
    try {
        return getDefaultTileSessionManager();
    } catch {
        return null;
    }
}

/**
 * Compute which acquisition_date a stack entry WOULD resolve to under the
 * current cursor. Used for dedupe in addLayer().
 *
 * Mirrors the resolver's snap logic but skipping the full RenderSpec build.
 */
export function effectiveAcquisitionDate(series, time_mode, pinned_date, cursor) {
    const cursorDate = time_mode === "pinned" ? pinned_date : cursor.global_date;
    if (!cursorDate || !series.acquisitions.length) return null;

    // Same on-or-before walk as snapToAcquisition, through the same comparator.
    // A raw string <= here once disagreed with the resolver: "2026_06_07" <=
    // "2026_06" is false, so a month cursor never matched inside its month.
    let chosen = null;
    for (let i = series.acquisitions.length - 1; i >= 0; i--) {
        if (acquisitionOnOrBefore(series.acquisitions[i].acquisition_date, cursorDate)) {
            chosen = series.acquisitions[i];
            break;
        }
    }
    return chosen?.acquisition_date ?? null;
}

/**
 * Turn RenderSpec[] into the shape useLayerRegistryStore expects.
 * Each registry entry is keyed by layer_id so reorder/dedup is stable.
 *
 * Degraded specs are still registered (visible:false) so the UI can render
 * a layer card with a reason. The dataset_key may be null in that case;
 * downstream tile-session code must check for that before requesting tiles.
 */
function buildRegistryLayersFromSpecs(specs, catalog) {
    return specs.map((spec) => {
        const series = catalog.series.find((s) => s.series_key === spec.series_key);
        const acquisition = series?.acquisitions.find(
            (a) => a.acquisition_date === spec.effective_date
        );

        return {
            id: spec.layer_id,
            scope: "imagery",
            kind: "imagery",                              // ← match useSyncRegistryLayers contract
            name: spec.display_name,
            visible: spec.visible,
            z_index: spec.z_index,
            opacity: spec.opacity,
            // Per-layer raster adjustments — carried top-level like opacity so
            // useSyncRegistryLayers reads it off the registry entry. Identity
            // fallback keeps legacy specs (no adjust) rendering untouched.
            adjust: spec.adjust ?? makeIdentityAdjust(),
            minzoom: acquisition?.minzoom ?? 0,           // ← top-level, not under meta
            maxzoom: acquisition?.maxzoom ?? 22,
            source: {
                type: "raster",                             // useSyncRegistryLayers checks for this
                dataset_key: spec.dataset_key,              // ← what syncImageryLayer reads
            },
            meta: {
                // Resolver-specific fields kept here for the UI to read.
                // The rendering pipeline only looks at `source` and top-level fields.
                effective_date: spec.effective_date,
                cursor_date: spec.cursor_date,
                staleness_months: spec.staleness_months,
                is_stale: spec.is_stale,
                scope: spec.scope,
                aoi_id: spec.aoi_id,
                degraded: spec.degraded,
                opacity: spec.opacity,
                adjust: spec.adjust ?? makeIdentityAdjust(),
                series_key: spec.series_key,
                basin: spec.basin,
                region: spec.region,
                sku_code: spec.sku_code,
                label: spec.display_name,
                acquisition_date: spec.effective_date,
                resolution: series?.resolution_cm != null ? `${series.resolution_cm}cm` : null,
                bounds: acquisition?.bounds ?? null,
            },
        };
    });
}

// ─────────────────────────────────────────────
// Default fetcher — uses fetch + your auth header convention
// ─────────────────────────────────────────────

async function defaultFootprintsFetcher({ operatingAoiId = null } = {}) {
    const store = useAuthStore.getState();
    const token = await store.ensureFreshToken();
    if (!token) throw new Error("no_auth_token");

    // lod=fine IS LOAD-BEARING, NOT A TUNING KNOB.
    //
    // authz-api defaults this parameter to "coarse" (index.js:667) and this
    // fetcher never passed it, so the catalog held ST_Envelope of each dataset:
    // ONE RECTANGLE, five vertices, 173 bytes. The fine footprint is the
        // dissolved union of the granules and the difference is not cosmetic.
        //
        // RE-MEASURED 2026-09-07, after the synthetic 2025_06_01 was deleted — its
        // row here read 76,202 / 6,135 / 8.1% and the dataset no longer exists:
        //
        //     dataset        coarse km2   fine km2   real    vertices
        //     2025_09_18         12,043      7,015   58.2%        784
        //     2025_12_13        117,819      9,886    8.4%      2,541
        //     2026_03_01            837        721   86.1%        159
        //
        // THE RATIO IS NOT A CONSTANT, and the old line saying "roughly 12x" was
        // reading two datasets that happened to agree. It is 11.9x for 2025_12_13,
        // 1.7x for 2025_09_18 and 1.2x for 2026_03_01 — the envelope over-reports in
        // proportion to how SCATTERED a collect is, so the widest and most fragmented
        // one is punished hardest and a compact enclave barely at all. That is the
        // shape to expect, not a fixed multiplier.
        //
        // Measured at -104.0/31.9, coarse said BOTH of the then-current datasets
        // covered and fine said NEITHER did.
    // Anything asking "does this date have imagery here" off the coarse
    // geometry gets a confident yes over ground that renders blank.
    //
    // COST: 173 bytes -> 44-64 kB per dataset, ~108 kB for the current corpus,
    // fetched once at catalog load rather than per pan.
    //
    // SAFE FOR EXISTING CONSUMERS, and provably rather than probably: the only
    // thing derived from the footprint today is `bounds`, and
    // groupFootprintsIntoSeries computes that from `coverage_bounds` — a column
    // on `datasets`, independent of lod — not from this geometry. Separately,
    // ST_Envelope(fine) equals the coarse geometry exactly for all three
    // datasets, so even a bounds derived from this field would be unchanged.
    const params = new URLSearchParams({ lod: "fine" });
    if (operatingAoiId) {
        params.set("operating_aoi", operatingAoiId);
    }
    const url = `/imagery/footprints?${params}`;

    const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        throw new Error(`footprints fetch failed: ${res.status}`);
    }
    return res.json();
}
